<template>
  <OrderLayout
    :is-loading="isLoadingStoreData"
    :error="null"
    :store="store"
    :hero-store="heroStore"
    :selected-location="selectedLocation"
    :cart-count="totalItems"
    :cart-items="cartItems"
    :is-sidebar-visible="isSidebarVisible"
    :is-cart-open="isCartOpen"
    :is-cart-pinned="isCartPinned"
    :filtered-menu="filteredMenu"
    :selected-item="selectedItem"
    :order-config="orderConfig"
    :is-store-closed="isStoreClosed"
    :on-cart-click="handleCartClick"
    :on-cart-close="handleCartClose"
    :on-update-quantity="handleUpdateQuantity"
    :on-remove-item="handleRemoveItem"
    :on-checkout="handleCheckout"
    :on-pin="handlePin"
    :on-unpin="handleUnpin"
    :on-item-click="setSelectedItem"
    :on-buy-now-click="handleBuyNowClick"
    :on-item-modal-close="handleItemModalClose"
    :on-add-to-cart="handleAddToCart"
    :is-buy-now-enabled="isBuyNowEnabled"
    :editing-cart-id="editingCartId"
    :on-edit-item="handleEditItem"
    :on-update-cart-item="handleUpdateCartItem"
  >
    <template #before-menu>
      <AnnouncementBanner />
      <PromotionBanners />
    </template>
  </OrderLayout>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex";
import OrderLayout from "@/layouts/OrderLayout.vue";
import { useOrderViewInitialization } from "@/composables/useOrderViewInitialization";
import { useSelectedStore } from "@/composables/useSelectedStore";
import { usePersistedOrderSettings } from "@/composables/usePersistedOrderSettings";
import { useCartManagement } from "@/composables/useCartManagement";
import { useModalManagement } from "@/composables/useModalManagement";
import { useCategoryNavigation } from "@/composables/useCategoryNavigation";
import { useStoreStatusMonitoring } from "@/composables/useStoreStatusMonitoring";
import { useOrderViewLoadingState } from "@/composables/useOrderViewLoadingState";
import PromotionBanners from "@/components/PromotionBanners.vue";
import AnnouncementBanner from "@/components/AnnouncementBanner.vue";
import { getProductModifierGroups } from "@/helpers/apiHelper";
import { resolveStoreAlias } from "@/helpers/urlHelper";

export default {
  name: "OrderView",
  components: {
    OrderLayout,
    PromotionBanners,
    AnnouncementBanner
  },
  created() {
    this._initialization = useOrderViewInitialization(
      this.$store,
      this.$router
    );
    this._selectedStore = useSelectedStore(this.$store);
    this._persistedOrderSettings = usePersistedOrderSettings(
      this.$store,
      this._selectedStore
    );
    this._cartManagement = useCartManagement(this.$store, this.$router);
    this._modalManagement = useModalManagement(this.$store);
    this._categoryNavigation = useCategoryNavigation(this.$store);
    this._storeStatusMonitoring = useStoreStatusMonitoring(
      this.$store,
      this._selectedStore
    );
    this._loadingState = useOrderViewLoadingState(
      this.$store,
      this._selectedStore
    );
  },
  async beforeMount() {
    await this.initializeForStoreAlias(this.store.alias);
  },
  mounted() {
    this._storeStatusMonitoring.startMonitoring();

    this.handleCategoryFromUrl();

    this._categoryNavigation.setupHashChangeListener(
      this.handleCategoryFromUrl
    );

    this.$nextTick(() => {
      this.syncDocumentTitle();
      this.updateSeoMeta();
    });
  },
  beforeDestroy() {
    this._storeStatusMonitoring.stopMonitoring();

    this._categoryNavigation.removeHashChangeListener(
      this.handleCategoryFromUrl
    );
  },
  watch: {
    selectedLocation(newVal) {
      if (!newVal) {
        this._modalManagement.setLocationModalOpen(true);
      }
      this.$nextTick(() => {
        this.syncDocumentTitle();
        this.updateSeoMeta();
      });
    },
    async "$route.params.storeAlias"(newAlias, oldAlias) {
      if (newAlias && newAlias !== oldAlias) {
        await this.initializeForStoreAlias(newAlias);
      }
    },
    $route() {
      this.handleCategoryFromUrl();
      this.$nextTick(() => {
        this.syncDocumentTitle();
        this.updateSeoMeta();
      });
    }
  },
  methods: {
    ...mapMutations(["SET_ORDER_CONFIG"]),
    async initializeForStoreAlias(storeAlias) {
      try {
        await this._initialization.initialize(storeAlias);

        this.syncDocumentTitle();
        this.updateSeoMeta();

        this.loadPersistedOrderSettings();
      } catch (error) {
        // Error handling is done in composable (redirects to NotFound if 404)
      }
    },
    getStoreDisplayName(store) {
      return (
        store?.displayName ||
        store?.display_name ||
        store?.displayname ||
        store?.location_name ||
        store?.name ||
        ""
      );
    },
    getLocationDisplayName(location) {
      return (
        location?.displayName ||
        location?.display_name ||
        location?.displayname ||
        location?.location_name ||
        ""
      );
    },
    getDisplayStoreName() {
      const locationName = this.getLocationDisplayName(this.selectedLocation);
      if (locationName) {
        return locationName;
      }
      return this.getStoreDisplayName(this.store);
    },
    setMeta(name, content) {
      if (!name) return;
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content || "");
    },
    getRestaurantBrandImage() {
      const restaurantBrand = document.querySelector(".restaurant-brand");
      if (!restaurantBrand) return "";

      const dataImageUrl = restaurantBrand.getAttribute("data-image-url");
      if (dataImageUrl) return dataImageUrl;

      if (restaurantBrand.tagName === "IMG" && restaurantBrand.src) {
        return restaurantBrand.src;
      }

      const brandImage = restaurantBrand.querySelector("img");
      if (brandImage && brandImage.src) return brandImage.src;

      return "";
    },
    upsertRestaurantSchema(schemaData) {
      if (!schemaData) return;

      const scriptId = "restaurant-ld-json";
      let script = document.getElementById(scriptId);

      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(schemaData);
    },
    updateSeoMeta() {
      const storeData = this.$store.getters.getStore || {};
      const location = this.selectedLocation || {};
      const storeName = this.getDisplayStoreName();
      const lastUsedAlias = resolveStoreAlias(this.$route);
      const address = location.address || storeData.address || "";
      const city = location.city || storeData.city || "";
      const state = location.state || storeData.state || "";
      const brandImage = this.getRestaurantBrandImage();

      this.setMeta(
        "description",
        `Order Online from ${storeName} | ${address} ${city}, ${state} Powered by Biyo POS.`
      );

      this.upsertRestaurantSchema({
        "@context": "https://schema.org",
        "@type": "Restaurant",
        name: `Biyo POS • ${storeName}`,
        url: `https://order.biyo.co/${lastUsedAlias}`,
        image: brandImage
      });
    },
    // Handlers para el layout
    handleCartClick() {
      this._cartManagement.setCartOpen(true);
    },
    handleCartClose() {
      this._cartManagement.setCartOpen(false);
    },
    handleUpdateQuantity(itemId, quantity) {
      this._cartManagement.updateQuantity(itemId, quantity);
    },
    handleRemoveItem(itemId) {
      this._cartManagement.removeItem(itemId);
    },
    handleCheckout() {
      this._cartManagement.checkout(this.store.alias);
    },
    handlePin() {
      this._cartManagement.setCartPinned(true, this.cartItems);
    },
    handleUnpin() {
      this._cartManagement.setCartPinned(false, this.cartItems);
    },
    setSelectedItem(item) {
      this._modalManagement.setItemModalOpen(item);
    },
    async handleBuyNowClick(item) {
      if (item.requiresConfirmation) {
        this._modalManagement.setItemModalOpen(item);
        return;
      }

      if (
        item.has_modifier_groups === true ||
        item.hasModifierGroups === true
      ) {
        this._modalManagement.setItemModalOpen(item);
        return;
      }

      if (
        item.has_modifier_groups === undefined &&
        item.hasModifierGroups === undefined
      ) {
        try {
          const modifierGroups = await getProductModifierGroups(item.id);
          if (modifierGroups.length > 0) {
            this._modalManagement.setItemModalOpen({
              ...item,
              modifier_groups: modifierGroups
            });
            return;
          }
        } catch (error) {
          this._modalManagement.setItemModalOpen(item);
          return;
        }
      }

      this._cartManagement.addToCart({
        item,
        quantity: 1,
        notes: "",
        modifiers: []
      });
    },
    handleItemModalClose() {
      this._modalManagement.setItemModalOpen(null);
      this.editingCartId = null;
    },
    handleAddToCart(item) {
      this._cartManagement.addToCart(item);
    },
    handleEditItem(cartItem) {
      this._cartManagement.setCartOpen(false);
      this._modalManagement.setItemModalOpen({
        ...cartItem,
        notes: cartItem.note,
        modifiers: cartItem.selectedModifiers
      });
      this.editingCartId = cartItem.cartId;
    },
    handleUpdateCartItem(cartId, payload) {
      this._cartManagement.updateCartItem(cartId, payload);
      this.editingCartId = null;
    },
    handleCategoryFromUrl() {
      this._categoryNavigation.handleCategoryFromUrl();
    },
    loadPersistedOrderSettings() {
      if (this._persistedOrderSettings) {
        this._persistedOrderSettings.loadPersistedOrderSettings(
          this.SET_ORDER_CONFIG,
          this.orderConfig
        );
      }
    },
    syncDocumentTitle() {
      const storeName = this.getDisplayStoreName();
      const location = this.selectedLocation || {};
      const storeData = this.$store.getters.getStore || {};
      const city = location.city || storeData.city || "";
      const state = location.state || storeData.state || "";
      const locationLabel = [city, state].filter(Boolean).join(", ");
      document.title =
        storeName && storeName.toString().trim()
          ? locationLabel
            ? `${storeName} - ${locationLabel} | Online Ordering | Biyo POS`
            : `${storeName} | Online Ordering | Biyo POS`
          : "Online Ordering | Biyo POS";
    }
  },
  data() {
    return {
      editingCartId: null
    };
  },
  computed: {
    ...mapState(["items"]),
    ...mapGetters([
      "totalItems",
      "isSidebarVisible",
      "filteredMenu",
      "orderConfig",
      "selectedItem",
      "cartItems",
      "isCartOpen",
      "isCartPinned",
      "getIsStoreClosed"
    ]),
    selectedLocation() {
      if (this._selectedStore) {
        return this._selectedStore.getSelectedStore();
      }
      return this.$store.getters.selectedLocation;
    },

    isStoreClosed() {
      if (this._selectedStore) {
        return this._selectedStore.isStoreClosed();
      }
      return this.getIsStoreClosed;
    },

    isBuyNowEnabled() {
      return this.$store.getters.getStore?.orderLiveSettings?.ooBuyNow === true;
    },

    store() {
      const stores = this.$store.getters.getStores || [];
      const storeData = this.$store.getters.getStore || {};
      return {
        name:
          this.getStoreDisplayName(storeData) ||
          this.$store.getters.getStoreName ||
          "",
        logo: storeData.logo || storeData.image || "",
        storesCount: stores.length,
        alias: resolveStoreAlias(this.$route)
      };
    },
    heroStore() {
      const storeData = this.$store.getters.getStore || {};
      const { formatLocationAddress } = require("@/utils/formatters");

      // Get address
      let address = "";
      if (this.selectedLocation) {
        address = formatLocationAddress(this.selectedLocation);
      } else {
        address = storeData.address || "";
      }

      // Get phone
      let phone = "";
      if (this.selectedLocation && this.selectedLocation.phone) {
        phone = this.selectedLocation.phone;
      } else {
        phone = storeData.phone || "";
      }

      // Get image
      let image = null;
      if (this.selectedLocation) {
        image =
          this.selectedLocation.online_ordering_image ||
          this.selectedLocation.onlineOrderingImage ||
          storeData.online_ordering_image ||
          storeData.onlineOrderingImage ||
          this.selectedLocation.image ||
          this.selectedLocation.logo ||
          this.selectedLocation.photo_url ||
          this.selectedLocation.photo_thumbnail ||
          storeData.image ||
          this.store.logo ||
          null;
      } else {
        image =
          storeData.online_ordering_image ||
          storeData.onlineOrderingImage ||
          storeData.image ||
          this.store.logo ||
          null;
      }

      // Get description
      let description = "";
      if (this.selectedLocation && this.selectedLocation.description) {
        description = this.selectedLocation.description;
      } else {
        description = storeData.description || "";
      }

      // Get rating
      let rating = null;
      if (this.selectedLocation && this.selectedLocation.rating) {
        rating = this.selectedLocation.rating;
      } else {
        rating = storeData.rating || null;
      }

      // Get working hours
      let workingHours = null;
      if (this._selectedStore) {
        const hours = this._selectedStore.getStoreWorkingHours();
        workingHours = hours === "Not Available" ? null : hours;
      }

      return {
        name: this.getDisplayStoreName(),
        address,
        phone,
        image,
        description,
        rating,
        workingHours
      };
    },
    isLoadingStoreData() {
      return this._loadingState.isLoadingStoreData();
    }
  }
};
</script>
